import React, { useEffect, useRef } from 'react';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import '../styles/syntax-highlighting.css';
import { useStore } from '../store/useStore';

// Register languages
hljs.registerLanguage('json', json);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);

interface SyntaxHighlighterProps {
  code: string;
  language?: 'json' | 'javascript' | 'python' | 'auto';
  className?: string;
  maxHeight?: string;
}

export const SyntaxHighlighter: React.FC<SyntaxHighlighterProps> = ({
  code,
  language = 'auto',
  className = '',
  maxHeight,
}) => {
  const codeRef = useRef<HTMLElement>(null);
  const { isDarkMode } = useStore();

  useEffect(() => {
    if (codeRef.current) {
      // Clear previous highlighting
      codeRef.current.removeAttribute('data-highlighted');

      if (language === 'auto') {
        hljs.highlightElement(codeRef.current);
      } else {
        const result = hljs.highlight(code, { language });
        codeRef.current.innerHTML = result.value;
      }
    }
  }, [code, language]);

  const containerStyle = maxHeight
    ? { maxHeight, overflowY: 'auto' as const }
    : {};

  // Use theme-appropriate background color
  const bgClass = isDarkMode
    ? 'bg-[#0d1117]'
    : 'bg-[#f6f8fa]';

  return (
    <div className={`relative ${className}`} style={containerStyle}>
      <pre className={`text-xs ${bgClass} p-4 rounded-lg border border-border overflow-x-auto`}>
        <code
          ref={codeRef}
          className={`hljs ${language !== 'auto' ? `language-${language}` : ''}`}
        >
          {code}
        </code>
      </pre>
    </div>
  );
};
